/**
 * Life-link VFX — soft ribbon + pulsed beads traveling A→B (not a projectile orb).
 * Phaser presentation only; endpoints/colors/duration come from preset + play ctx.
 */
import Phaser from "phaser";
import type { VfxLinkPreset } from "./types";

function parseColor(raw: string): number {
  return Number.parseInt(raw.replace(/^0x/i, ""), 16);
}

export type LifeLinkEndpoints = {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
};

/** Sample t∈[0,1] along segment (pure; Vitest-friendly). */
export function sampleSegment(
  ends: LifeLinkEndpoints,
  t: number,
): { x: number; y: number } {
  const u = Math.min(1, Math.max(0, t));
  return {
    x: ends.x0 + (ends.x1 - ends.x0) * u,
    y: ends.y0 + (ends.y1 - ends.y0) * u,
  };
}

/**
 * Draw a life-link: soft glow ribbon that fades, plus staggered beads
 * that travel caster → target along the segment.
 */
export function playLifeLink(
  scene: Phaser.Scene,
  ends: LifeLinkEndpoints,
  preset: VfxLinkPreset,
  depthOverride?: number,
): void {
  const color = parseColor(preset.color);
  const colorHot = parseColor(preset.colorHot ?? preset.color);
  const duration = Math.max(80, preset.duration);
  const depth = depthOverride ?? preset.depth ?? 11;
  const alpha0 = preset.alphaFrom ?? 0.72;
  const width = Math.max(1.5, preset.width ?? 5);
  const pulses = Math.max(0, Math.floor(preset.pulses ?? 5));
  const pulseR = Math.max(1.2, preset.pulseRadius ?? 3.2);
  const pulseGap = Math.max(0, preset.pulseGapMs ?? 55);
  const travelMs = Math.max(
    80,
    preset.travelMs ?? Math.round(duration * 0.58),
  );

  const dx = ends.x1 - ends.x0;
  const dy = ends.y1 - ends.y0;
  const dist = Math.hypot(dx, dy);
  if (!(dist > 1e-3)) return;

  // Soft ribbon (outer glow + hot core) — fades as a whole; not collision travel.
  const ribbon = scene.add.graphics().setDepth(depth);
  ribbon.lineStyle(width * 2.4, color, alpha0 * 0.35);
  ribbon.beginPath();
  ribbon.moveTo(ends.x0, ends.y0);
  ribbon.lineTo(ends.x1, ends.y1);
  ribbon.strokePath();
  ribbon.lineStyle(width, colorHot, alpha0 * 0.85);
  ribbon.beginPath();
  ribbon.moveTo(ends.x0, ends.y0);
  ribbon.lineTo(ends.x1, ends.y1);
  ribbon.strokePath();
  scene.tweens.add({
    targets: ribbon,
    alpha: 0,
    duration,
    ease: "Cubic.easeOut",
    onComplete: () => ribbon.destroy(),
  });

  // Traveling wave beads A→B (staggered), plus brief mid-path spark rings.
  for (let i = 0; i < pulses; i++) {
    const delay = i * pulseGap;
    const bead = scene.add
      .circle(ends.x0, ends.y0, pulseR, colorHot, alpha0)
      .setDepth(depth + 1);
    const hot = i % 2 === 0;
    if (!hot) bead.setFillStyle(color, alpha0 * 0.9);

    scene.tweens.add({
      targets: bead,
      x: ends.x1,
      y: ends.y1,
      alpha: 0.15,
      scale: 0.45,
      delay,
      duration: travelMs,
      ease: "Sine.easeInOut",
      onComplete: () => bead.destroy(),
    });

    // Pulsed ring that blooms once the bead reaches ~mid / near target.
    const bloomAt = delay + Math.round(travelMs * (0.45 + (i % 3) * 0.12));
    scene.time.delayedCall(bloomAt, () => {
      const t = Math.min(1, (bloomAt - delay) / Math.max(1, travelMs));
      const p = sampleSegment(ends, t);
      const ring = scene.add
        .circle(p.x, p.y, pulseR * 0.9, color, alpha0 * 0.55)
        .setDepth(depth);
      scene.tweens.add({
        targets: ring,
        scale: 2.4,
        alpha: 0,
        duration: Math.min(220, duration * 0.4),
        ease: "Cubic.easeOut",
        onComplete: () => ring.destroy(),
      });
    });
  }
}
